import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// Blog building blocks shared by the index (client filter) and the article page.

export const AUTHOR = {
  name: 'Richard Otto',
  role: 'CEO & Founder, Otti One',
  bio: 'Gründer von Otti One und Immobilienunternehmer. Er schreibt über Digitalisierung, Immobilienverwaltung und Schweizer Markttrends.',
  image:
    'https://res.cloudinary.com/dphbnwjtx/image/upload/c_thumb,g_face,w_200,h_200,f_webp,q_auto/v1758872167/Richard_H_Hi_Res_buw7fp.webp',
}

export function PostMeta({ post, className }) {
  return (
    <p
      className={cn(
        'flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs text-graphite',
        className
      )}
    >
      <time dateTime={post.dateISO}>{post.date}</time>
      <span aria-hidden>·</span>
      <span>{post.readTime} Lesezeit</span>
    </p>
  )
}

export function CategoryTag({ children, className }) {
  return (
    <span
      className={cn(
        'rounded-[3px] bg-surface/90 px-1.5 py-0.5 font-mono text-xs text-ink backdrop-blur-sm',
        className
      )}
    >
      {children}
    </span>
  )
}

// Underline that grows under the link label when the card is hovered.
const GROW_UNDERLINE =
  'bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_1px] bg-left-bottom bg-no-repeat pb-0.5 transition-[background-size] duration-300 group-hover:bg-[length:100%_1px]'

const CARD_HOVER =
  'transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-rule-strong hover:shadow-[0_24px_48px_-32px_rgb(var(--ink)/0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-canvas'

export function PostCard({ post, headingLevel: Heading = 'h3' }) {
  return (
    <Link
      href={`/blog/${post.slug}`}
      className={cn(
        'group flex h-full flex-col overflow-hidden rounded-card border border-rule bg-surface',
        CARD_HOVER
      )}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-mist">
        {post.image && (
          <Image
            src={post.image}
            alt=""
            fill
            sizes="(max-width: 768px) 100vw, 580px"
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          />
        )}
        <CategoryTag className="absolute left-3 top-3">
          {post.category}
        </CategoryTag>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <PostMeta post={post} />
        <Heading className="text-balance hyphens-manual text-[21px] font-[450] leading-[1.25] text-ink [font-stretch:106%]">
          {post.title}
        </Heading>
        <div className="flex-1">
          <p className="line-clamp-2 text-[15px] leading-relaxed text-graphite">
            {post.excerpt}
          </p>
        </div>
        <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
          <span className={GROW_UNDERLINE}>Weiterlesen</span>
          <ArrowRight
            aria-hidden
            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
          />
        </span>
      </div>
    </Link>
  )
}

export function FeaturedPost({ post, label }) {
  return (
    <Link
      href={`/blog/${post.slug}`}
      className={cn(
        'group grid overflow-hidden rounded-card border border-rule-strong bg-surface lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]',
        CARD_HOVER
      )}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-mist lg:aspect-auto lg:min-h-[420px]">
        {post.image && (
          <Image
            src={post.image}
            alt=""
            fill
            priority
            sizes="(max-width: 1024px) 100vw, 680px"
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          />
        )}
        <CategoryTag className="absolute left-4 top-4">
          {post.category}
        </CategoryTag>
      </div>
      <div className="flex flex-col justify-between gap-8 p-6 sm:p-10">
        <div className="flex flex-col gap-4">
          {label && (
            <p className="flex items-center gap-2 font-mono text-xs text-signal">
              <span aria-hidden className="relative flex h-2 w-2">
                <span className="ping-soft absolute inset-0 rounded-full bg-signal" />
                <span className="relative h-2 w-2 rounded-full bg-signal" />
              </span>
              {label}
            </p>
          )}
          <h2 className="text-balance hyphens-manual text-[28px] font-[450] leading-[1.15] text-ink [font-stretch:110%] sm:text-[34px]">
            {post.title}
          </h2>
          <p className="text-[17px] leading-relaxed text-graphite">
            {post.excerpt}
          </p>
          <PostMeta post={post} />
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-rule pt-5">
          <span className="flex items-center gap-3">
            <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full border border-rule-strong">
              <Image
                src={AUTHOR.image}
                alt=""
                fill
                sizes="36px"
                className="object-cover"
              />
            </span>
            <span className="flex flex-col text-sm leading-tight">
              <span className="font-semibold text-ink">{AUTHOR.name}</span>
              <span className="text-graphite">CEO & Founder</span>
            </span>
          </span>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-rule-strong text-primary transition-colors duration-300 group-hover:border-primary group-hover:bg-primary group-hover:text-white">
            <ArrowRight
              aria-hidden
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
            />
          </span>
        </div>
      </div>
    </Link>
  )
}
